import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, LogOut, FolderOpen, X, Menu, User } from 'lucide-react';
import { LanguageContext } from '../../context/LanguageContext';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useAuth } from '../../context/AuthContext';
import { clearLocalSession, redirectToUnifiedAuth } from '../../utils/unifiedAuth';
import ClientTicketNotifier from '../ClientTicketNotifier/ClientTicketNotifier';
import styles from './Header.module.css';
import snowImage from './snow.png';

const logoSrc = '/logo.png?v=plain-20260601';

const Header = ({
  onToggleSidebar = () => {},
  isSidebarOpen = false,
  isMobile = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleLanguage, language } = useContext(LanguageContext);
  const { user, loading } = useAuth();

  // Автоматический перевод строк
  const projectNotSelected = useAutoTranslate("Проект не выбран");
  const changeProject = useAutoTranslate("Сменить проект");
  const changeProjectShort = useAutoTranslate("Сменить");
  const searchPlaceholder = useAutoTranslate("Отсканируйте серийный номер чтобы взять в работу...");
  const logoutText = useAutoTranslate("Выйти");

  // Текст для кнопки переключения языка
  const languageToggleText = language === 'ru' ? "EN" : "RU";

  useEffect(() => {
    const nameFromStorage = localStorage.getItem('user_full_name');
    const photoFromStorage = localStorage.getItem('user_profile_photo');

    if (nameFromStorage) {
      setUserFullName(nameFromStorage);
      console.log('Header: Загружено имя пользователя из localStorage:', nameFromStorage);
    } else if (user?.full_name) {
      setUserFullName(user.full_name);
      console.log('Header: Загружено имя пользователя из контекста:', user.full_name);
    } else {
      setUserFullName('Пользователь');
    }

    if (photoFromStorage) {
      setProfilePhoto(photoFromStorage);
      console.log('Header: Загружено фото профиля из localStorage');
    }
  }, []);

  useEffect(() => {
    if (user?.full_name) {
      setUserFullName(user.full_name);
      localStorage.setItem('user_full_name', user.full_name);
      console.log('Header: Обновлено имя пользователя из контекста:', user.full_name);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setProfilePhoto('');
      localStorage.removeItem('user_profile_photo');
      return;
    }

    if (user.profile_photo !== undefined) {
      const photo = user.profile_photo || '';
      setProfilePhoto(photo);
      localStorage.setItem('user_profile_photo', photo);
      console.log('Header: Обновлено фото профиля из контекста');
    }
  }, [user]);

  useEffect(() => {
    const projectId = localStorage.getItem('selected_project_id');
    let projectName = localStorage.getItem('selected_project_name') || '';

    if (projectId && !projectName) {
      let availableProjects = user?.projects || [];
      if (!availableProjects.length) {
        try {
          availableProjects = JSON.parse(localStorage.getItem('user_projects') || '[]');
        } catch {
          availableProjects = [];
        }
      }
      const project = availableProjects.find(item => String(item.id) === String(projectId));
      if (project?.name) {
        projectName = project.name;
        localStorage.setItem('selected_project_name', projectName);
      }
    }

    if (!projectId && projectName) {
      localStorage.removeItem('selected_project_name');
      projectName = '';
    }

    setSelectedProject(projectName);
    console.log('Header: Загружен проект:', projectName);
  }, [location, user]);

  useEffect(() => {
    setIsMobileActionsOpen(false);
    setIsMobileSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    console.log('Header: Состояние AuthContext:', { user, loading });
  }, [user, loading]);

  const getInitials = (name) => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length > 1) {
      return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/serial/${searchTerm.trim()}`);
      setSearchTerm('');
      setIsMobileSearchOpen(false);
    }
  };

  const handleLogout = () => {
    console.log('Header: Выполняется выход');
    clearLocalSession();
    redirectToUnifiedAuth('/');
  };

  const handleChangeProject = () => {
    console.log('Header: Смена проекта');
    localStorage.removeItem('selected_project_id');
    localStorage.removeItem('selected_project_name');
    navigate('/select-project');
  };

  const handleOpenProfile = () => {
    navigate('/profile');
  };

  const toggleMobileSearch = () => {
    setIsMobileSearchOpen(!isMobileSearchOpen);
  };

  const toggleMobileActions = () => {
    setIsMobileActionsOpen((prev) => !prev);
  };

  const closeMobileActions = () => {
    setIsMobileActionsOpen(false);
  };

  if (loading) {
    return <div>Загрузка...</div>;
  }

  const roleNames = (user?.roles || []).map(role => role.name);
  const isGuest = roleNames.includes('Гость');

  const renderMobileActionsSheet = () => (
    <div className={styles.mobileActionsOverlay} onClick={closeMobileActions}>
      <div className={styles.mobileActionsSheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.mobileActionsHeader}>
          <div className={styles.mobileActionsUser}>
            <div className={`${styles.userAvatar} ${profilePhoto ? styles.userAvatarImage : ''}`}>
              {profilePhoto ? (
                <img src={profilePhoto} alt="Фото профиля" />
              ) : (
                getInitials(userFullName)
              )}
            </div>
            <div>
              <p className={styles.mobileUserName}>{userFullName}</p>
              <p className={styles.mobileUserRole}>{selectedProject || projectNotSelected}</p>
            </div>
          </div>
          <button type="button" className={styles.mobileCloseButton} onClick={closeMobileActions}>
            <X size={18} />
          </button>
        </div>

        <div className={styles.mobileActionsGroup}>
          <button type="button" className={styles.mobileActionButton} onClick={handleChangeProject}>
            <FolderOpen size={18} />
            <span>{changeProject}</span>
          </button>
          <button type="button" className={styles.mobileActionButton} onClick={handleOpenProfile}>
            <User size={18} />
            <span>Личный кабинет</span>
          </button>
          <button type="button" className={styles.mobileActionButton} onClick={toggleLanguage}>
            <span className={styles.languageBadge}>{languageToggleText}</span>
            <span>Сменить язык</span>
          </button>
        </div>

        <button type="button" className={styles.mobileLogoutButton} onClick={handleLogout}>
          <LogOut size={18} />
          <span>{logoutText}</span>
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <header className={`${styles.header} ${styles.mobileHeader}`}>
        <div className={styles.mobileTopBar}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={onToggleSidebar}
            aria-label={isSidebarOpen ? 'Закрыть меню' : 'Открыть меню'}
          >
            {isSidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div className={styles.mobileLogo}>
            <img src={logoSrc} alt="CONNEXTA SYSTEM" />
          </div>
          {!isGuest && (
            <button
              type="button"
              className={styles.mobileIconButton}
              onClick={() => setIsMobileSearchOpen(true)}
              aria-label="Поиск"
            >
              <Search size={20} />
            </button>
          )}
          <button type="button" className={styles.mobileAvatarButton} onClick={toggleMobileActions}>
            <div className={`${styles.userAvatar} ${profilePhoto ? styles.userAvatarImage : ''}`}>
              {profilePhoto ? <img src={profilePhoto} alt="Фото профиля" /> : getInitials(userFullName)}
            </div>
          </button>
        </div>

        <div className={styles.mobileProjectRow}>
          <button type="button" className={styles.projectPill} onClick={handleChangeProject}>
            <span className={styles.projectName}>{selectedProject || projectNotSelected}</span>
            <FolderOpen size={16} />
          </button>
        </div>

        {isMobileSearchOpen && (
          <div className={styles.mobileSearchOverlay} onClick={toggleMobileSearch}>
            <form
              className={styles.mobileSearchSheet}
              onSubmit={handleSearchSubmit}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.mobileSearchHeader}>
                <span>Поиск по серийному номеру</span>
                <button type="button" onClick={toggleMobileSearch}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles.mobileSearchField}>
                <Search />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button type="submit" className={styles.mobileSearchSubmit}>
                Искать
              </button>
            </form>
          </div>
        )}

        {isMobileActionsOpen && renderMobileActionsSheet()}
        <div className={styles.snowDecoration} style={{ backgroundImage: `url(${snowImage})` }}></div>
      </header>
    );
  }

  return (
    <header className={styles.header}>
      <div className={styles.headerContainer}>
        <div className={styles.logoSection}>
          {isMobile && (
            <button
              type="button"
              className={styles.menuButton}
              onClick={onToggleSidebar}
              aria-label={isSidebarOpen ? 'Закрыть меню' : 'Открыть меню'}
            >
              {isSidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          )}
          <div className={styles.logoContainer}>
            <img src={logoSrc} alt="CONNEXTA SYSTEM" className={styles.logoImage} />
          </div>
        </div>

        <div className={styles.projectInfo}>
          {selectedProject ? (
            <>
              <span className={styles.projectName}>{selectedProject}</span>
              <button 
                className={styles.changeProjectButton} 
                onClick={handleChangeProject}
                title={changeProject}
              >
                <FolderOpen className={styles.changeProjectIcon} />
                <span className={styles.changeProjectText}>{changeProjectShort}</span>
              </button>
            </>
          ) : (
            <span className={styles.projectName}>{projectNotSelected}</span>
          )}
        </div>

        {!isGuest && ( // Скрываем поле поиска для гостя
          <form onSubmit={handleSearchSubmit} className={`${styles.searchForm} ${isMobileSearchOpen ? styles.mobileSearchOpen : ''}`}>
            <div className={styles.searchInputWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              <button 
                type="button" 
                className={styles.mobileSearchClose}
                onClick={toggleMobileSearch}
              >
                <X size={20} />
              </button>
            </div>
          </form>
        )}

        <div className={styles.userSection}>
          {!isGuest && (
            <button 
              className={styles.mobileSearchButton} 
              onClick={toggleMobileSearch}
              aria-label="Поиск"
            >
              <Search size={20} />
            </button>
          )}

          {!isMobile && (
          <div className={styles.languageToggle}>
            <button onClick={toggleLanguage} className={styles.languageToggleButton}>
              {languageToggleText}
            </button>
          </div>
          )}


          <button type="button" className={styles.userInfo} onClick={handleOpenProfile}>
            <div className={`${styles.userAvatar} ${profilePhoto ? styles.userAvatarImage : ''}`}>
              {profilePhoto ? (
                <img src={profilePhoto} alt="Фото профиля" />
              ) : (
                getInitials(userFullName)
              )}
            </div>
            <span className={styles.userName}>{userFullName}</span>
          </button>
          <ClientTicketNotifier />
          <button onClick={handleLogout} className={styles.logoutButton}>
            <LogOut className={styles.logoutIcon} />
            <span className={styles.logoutText}>{logoutText}</span>
          </button>
        </div>
      </div>
      <div className={styles.snowDecoration} style={{ backgroundImage: `url(${snowImage})` }}></div>
    </header>
  );
};

export default Header;
